// src/components/Trucks.js
import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { FaSearch, FaPen, FaTrashAlt, FaExclamationTriangle } from "react-icons/fa";
import {
  LuTruck,
  LuFuel,
  LuWrench,
  LuGauge,
  LuFileCheck,
  LuBadgeInfo,
  LuContainer,
} from "react-icons/lu";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "../axiosConfig";
import "../styles/Trucks.css";
import { useTranslation } from "react-i18next";
import { getSocket } from "../lib/socket";
import { getStoredToken, getStoredUser } from "../lib/authStorage";
import { useInstantListCache } from "../hooks/useInstantListCache";
import {
  isTruckDetailsCacheFresh,
  primeTruckDetailsCache,
  readTruckDetailsCache,
  readTruckSummaryCacheItems,
  TRUCK_SUMMARY_CACHE_UPDATED_EVENT,
  writeTruckDetailsCache,
} from "../utils/truckDetailsCache";

/* ------------------------------ small util ------------------------------ */
function useDebouncedValue(value, delay = 180) {
  const [deb, setDeb] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDeb(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return deb;
}

function decodePayload(tok) {
  try {
    const b64 = tok.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}

const safeParse = (s) => {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
};

function readFuelSummariesCache(
  cacheKey
) {
  if (
    typeof window ===
      "undefined" ||
    !cacheKey
  ) {
    return {};
  }

  try {
    const parsed =
      safeParse(
        window.localStorage.getItem(
          cacheKey
        )
      );

    const data =
      parsed &&
      typeof parsed ===
        "object" &&
      parsed.data &&
      typeof parsed.data ===
        "object"
        ? parsed.data
        : parsed;

    return data &&
      typeof data ===
        "object" &&
      !Array.isArray(
        data
      )
      ? data
      : {};
  } catch {
    return {};
  }
}

function writeFuelSummariesCache(
  cacheKey,
  summaries
) {
  if (
    typeof window ===
      "undefined" ||
    !cacheKey ||
    !summaries ||
    typeof summaries !==
      "object"
  ) {
    return;
  }

  try {
    window.localStorage.setItem(
      cacheKey,
      JSON.stringify({
        savedAt:
          Date.now(),

        data:
          summaries,
      })
    );
  } catch {
    // Cache indisponível.
  }
}

function resolveCompanyId() {
  const tok =
    getStoredToken() ||
    window.localStorage.getItem("authToken") ||
    "";
  const p = tok ? decodePayload(tok) : null;

  const fromJwt = p?.company_id ?? p?.companyId ?? null;
  if (Number.isFinite(Number(fromJwt)) && Number(fromJwt) > 0) {
    return Number(fromJwt);
  }

  const direct =
    window.sessionStorage.getItem("companyId") ||
    window.sessionStorage.getItem("company_id") ||
    window.localStorage.getItem("companyId") ||
    window.localStorage.getItem("company_id") ||
    null;

  if (Number.isFinite(Number(direct)) && Number(direct) > 0) {
    return Number(direct);
  }

  const storedUser = getStoredUser();
  const fromUser =
    storedUser?.company_id ??
    storedUser?.companyId ??
    null;

  if (Number.isFinite(Number(fromUser)) && Number(fromUser) > 0) {
    return Number(fromUser);
  }

  return null;
}

/* ------------------------------ card helpers ------------------------------ */
const pickValue = (...vals) =>
  vals.find(
    (v) => v !== undefined && v !== null && String(v).trim() !== ""
  );

function fmtDateISO(d) {
  if (!d) return "—";
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return String(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch {
    return String(d);
  }
}

function toFuelNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function fmtConsumption(value) {
  const n = toFuelNumber(value);
  if (n === null) return "—";
  return `${n.toFixed(2)} L/100km`;
}

const monthName = (m, t) => {
  const n = Number(m);
  if (!n || n < 1 || n > 12) return "—";
  return t(`months.${n}`);
};

function deriveDocExpiry(item) {
  const insp = item?.InspectionPhotoExpiry || null;
  const insurance = item?.TruckInsuranceExpiry || null;
  const reg = item?.VehicleRegistrationCertificateExpiry || null;
  const lic = item?.CommunityLicenceExpiry || null;

  return { insp, insurance, reg, lic };
}

function deriveInventory(item) {
  return {
    straps: Number(item?.number_of_straps ?? 0),
    ratchets: Number(item?.number_of_ratchets ?? 0),
    edge: Number(item?.number_of_edge_protectors ?? 0),
    antiSlip: Number(item?.number_of_anti_slip_mats ?? 0),
  };
}

function getIconToneClass(id) {
  const palettes = [
    "tone-cyan",
    "tone-violet",
    "tone-pink",
    "tone-emerald",
    "tone-amber",
    "tone-blue",
  ];

  const safeId = Number(id) || 0;
  return palettes[Math.abs(safeId) % palettes.length];
}

const DOC_EXPIRY_WINDOW_DAYS = 60;
const DAY_MS = 24 * 60 * 60 * 1000;

function daysUntil(dateValue) {
  if (!dateValue) return null;

  const dt = new Date(dateValue);
  if (Number.isNaN(dt.getTime())) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const target = new Date(dt);
  target.setHours(0, 0, 0, 0);

  return Math.ceil((target.getTime() - today.getTime()) / DAY_MS);
}

function getDocTone(dateValue) {
  const days = daysUntil(dateValue);

  if (days === null) return "empty";
  if (days < 0) return "expired";
  if (days <= DOC_EXPIRY_WINDOW_DAYS) return "expiring";

  return "ok";
}

function getTruckDocDates(item) {
  const docs = deriveDocExpiry(item);
  return [docs.insp, docs.insurance, docs.reg, docs.lic];
}

function getTrailerDocDates(item) {
  return [
    item?.inspectionExpiry,
    item?.vehicleRegistrationCertificateExpiry,
    item?.trailerInsuranceExpiry,
  ];
}

function getDocDates(item, view) {
  return view === "trucks" ? getTruckDocDates(item) : getTrailerDocDates(item);
}

function countExpiringDocs(items, view) {
  return (items || []).reduce((acc, item) => {
    const dates = getDocDates(item, view);
    return acc + dates.filter((date) => getDocTone(date) === "expiring").length;
  }, 0);
}

function countAttentionItems(items, view) {
  return (items || []).reduce((acc, item) => {
    const dates = getDocDates(item, view);
    const hasExpiredDoc = dates.some((date) => getDocTone(date) === "expired");
    return acc + (hasExpiredDoc ? 1 : 0);
  }, 0);
}

function getLastFuelLog(fuelSummary) {
  return (
    fuelSummary?.lastLog ||
    fuelSummary?.latestLog ||
    fuelSummary?.lastFuelLog ||
    null
  );
}

function getFuelConsumptionValue(fuelSummary) {
  const lastLog = getLastFuelLog(fuelSummary);

  return pickValue(
    fuelSummary?.lastConsumptionL100Km,
    fuelSummary?.lastConsumptionL100km,
    fuelSummary?.consumptionL100Km,
    fuelSummary?.consumptionL100km,
    fuelSummary?.averageConsumptionL100Km,
    fuelSummary?.averageConsumptionL100km,
    lastLog?.consumptionL100Km,
    lastLog?.consumptionL100km,
    null
  );
}

function hasCalculatedConsumption(fuelSummary) {
  return toFuelNumber(getFuelConsumptionValue(fuelSummary)) !== null;
}

function fmtMaybeLitres(value) {
  const n = toFuelNumber(value);
  if (n === null) return "—";
  return `${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)} L`;
}

function fmtMaybeMoney(value) {
  const n = toFuelNumber(value);
  if (n === null) return "—";
  return `${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)} €`;
}

function getVehicleStatus(item, t) {
  const raw = String(item?.status ?? item?.Status ?? "ACTIVE").toUpperCase();

  const isActive =
    raw !== "INACTIVE" &&
    raw !== "ARCHIVED" &&
    raw !== "DELETED" &&
    raw !== "CANCELLED";

  return {
    isActive,
    label: isActive
      ? t("status.active", "Ativo")
      : t("status.inactive", "Inativo"),
  };
}

function getTruckDocRows(item, t) {
  const docs = deriveDocExpiry(item);

  return [
    {
      label: t("card.docs_insp", "Inspeção"),
      value: fmtDateISO(docs.insp),
      tone: getDocTone(docs.insp),
    },
    {
      label: t("card.docs_insurance", "Seguro do camião"),
      value: fmtDateISO(docs.insurance),
      tone: getDocTone(docs.insurance),
    },
    {
      label: t("card.docs_reg", "Certificado de Matrícula"),
      value: fmtDateISO(docs.reg),
      tone: getDocTone(docs.reg),
    },
    {
      label: t("card.docs_lic", "Licença Comunitária"),
      value: fmtDateISO(docs.lic),
      tone: getDocTone(docs.lic),
    },
  ];
}

function getTrailerDocRows(item, t) {
  return [
    {
      label: t("card.inspection", "Inspeção"),
      value: fmtDateISO(item?.inspectionExpiry),
      tone: getDocTone(item?.inspectionExpiry),
    },
    {
      label: t("card.vrc", "Certificado de Matrícula"),
      value: fmtDateISO(item?.vehicleRegistrationCertificateExpiry),
      tone: getDocTone(item?.vehicleRegistrationCertificateExpiry),
    },
    {
      label: t("card.trailerInsurance", "Seguro do Reboque"),
      value: fmtDateISO(item?.trailerInsuranceExpiry),
      tone: getDocTone(item?.trailerInsuranceExpiry),
    },
  ];
}

function getTrailerInventory(item) {
  return {
    axles: Number(item?.numberOfAxles ?? 0),
    straps: Number(item?.numberOfStraps ?? 0),
    ratchets: Number(item?.numberOfRatchets ?? 0),
    woodenBeams: Number(item?.numberOfWoodenBeams ?? 0),
    edge: Number(item?.numberOfEdgeProtectors ?? 0),
    antiSlip: Number(item?.numberOfAntiSlippingMattresses ?? 0),
    sidebars: Number(item?.numberOfSidebars ?? 0),
    fireExtinguishers: Number(item?.numberOfFireExtinguishers ?? 0),
  };
}

function hasInventory(item, view) {
  const inv = view === "trucks" ? deriveInventory(item) : getTrailerInventory(item);
  return Object.values(inv).some((value) => Number(value) > 0);
}

function FleetStatCard({ tone, icon: Icon, title, value, subtitle }) {
  return (
    <div className={`trucks2-statCard trucks2-statCard--${tone}`}>
      <div className="trucks2-statIcon">
        <Icon />
      </div>

      <div className="trucks2-statText">
        <span>{title}</span>
        <strong>{value}</strong>
        <small>{subtitle}</small>
      </div>

      <div className="trucks2-sparkline" aria-hidden="true">
        <i />
        <i />
        <i />
        <i />
        <i />
      </div>
    </div>
  );
}

function CardSection({ icon: Icon, title, children }) {
  return (
    <section className="trucks2-section">
      <div className="trucks2-sectionTitle">
        <Icon />
        <span>{title}</span>
      </div>
      {children}
    </section>
  );
}

function DetailLine({ label, value, mono = false }) {
  return (
    <div className="trucks2-detailLine">
      <span>{label}</span>
      <strong className={mono ? "trucks2-mono" : ""} title={value}>
        {value}
      </strong>
    </div>
  );
}

function DocPanel({ rows }) {
  return (
    <div className="trucks2-docPanel">
      {rows.map((row) => (
        <div className="trucks2-docItem" key={row.label}>
          <span className="trucks2-docKey">{row.label}</span>
          <span className={`trucks2-docVal doc-${row.tone}`}>
            {row.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function InventoryGrid({ items }) {
  return (
    <div className="trucks2-invGrid">
      {items.map((item) => (
        <div className="trucks2-invCell" key={item.label}>
          <span className="trucks2-invKey">{item.label}</span>
          <strong className="trucks2-invNum">{item.value}</strong>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------ Card components ------------------------------ */
const TruckCard = React.memo(function TruckCard({
  item,
  fuelSummary,
  onOpen,
  onRemove,
  t,
}) {
  const iconToneClass = getIconToneClass(item?.id);
  const status = getVehicleStatus(item, t);

  const plate = pickValue(item?.plate, "—");
  const sub = pickValue(
    item?.brand && item?.model ? `${item.brand} ${item.model}` : null,
    item?.brand,
    item?.model,
    "—"
  );

  const yearText = item?.year ? String(item.year) : "—";
  const monthRaw = pickValue(item?.Month, item?.month, null);
  const monthText =
    monthRaw !== null &&
    monthRaw !== undefined &&
    String(monthRaw).trim() !== ""
      ? monthName(monthRaw, t)
      : "—";

  const yearMonthText =
    yearText !== "—" && monthText !== "—"
      ? `${yearText} • ${monthText}`
      : yearText;

  const gasType = pickValue(item?.type_of_gas, "—");
  const supportFuel = pickValue(
    item?.support_fuel,
    item?.supportFuel,
    item?.supportFuelType,
    "—"
  );
  const vinText = pickValue(item?.vin, "—");

  const lastLog = getLastFuelLog(fuelSummary);
  const lastConsumption = getFuelConsumptionValue(fuelSummary);
  const hasConsumption = toFuelNumber(lastConsumption) !== null;

  const fuelStatusClass = fuelSummary?.pendingSinceLastFullTank
    ? "pending"
    : hasConsumption
    ? "ok"
    : "na";

  const fuelStatusText = fuelSummary?.pendingSinceLastFullTank
    ? t("fuel.pendingShort", "Pendente")
    : hasConsumption
    ? t("fuel.calculated", "Calculado")
    : t("fuel.noCalculatedCycle", "Sem ciclo completo");

  const fuelConsumptionText = hasConsumption
    ? fmtConsumption(lastConsumption)
    : "—";

  const lastOdometerText = pickValue(
    lastLog?.odometerKm,
    fuelSummary?.lastOdometerKm,
    fuelSummary?.odometerKm,
    null
  );

  const lastLitresText = pickValue(
    lastLog?.litresAdded,
    fuelSummary?.lastLitresAdded,
    fuelSummary?.litresAdded,
    null
  );

  const lastTotalCostText = pickValue(
    lastLog?.totalCost,
    fuelSummary?.lastTotalCost,
    fuelSummary?.totalCost,
    null
  );

  const lastFuelDateText = pickValue(
    lastLog?.date,
    fuelSummary?.lastFuelDate,
    fuelSummary?.date,
    null
  );

  const lastFuelHourText = pickValue(
    lastLog?.hour,
    fuelSummary?.lastFuelHour,
    fuelSummary?.hour,
    null
  );

  const lastFuelMetaText =
    lastFuelDateText || lastFuelHourText
      ? `${fmtDateISO(lastFuelDateText)}${lastFuelHourText ? ` ${lastFuelHourText}` : ""}`
      : "—";

  const docsRows = getTruckDocRows(item, t);
  const inv = deriveInventory(item);

  const inventoryItems = [
    { label: t("card.straps", "Nº de Cintas"), value: inv.straps },
    { label: t("card.edge", "Nº de Cantos"), value: inv.edge },
    { label: t("card.ratchets", "Nº de Catracas"), value: inv.ratchets },
    { label: t("card.antiSlip", "Nº de Antiderrapantes"), value: inv.antiSlip },
  ];

  return (
    <article
      className="trucks2-card"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen?.(e)}
    >
      <header className="trucks2-top">
        <div className="trucks2-identity">
          <div className={`trucks2-iconbox ${iconToneClass}`}>
            <LuTruck />
          </div>

          <div className="trucks2-titlewrap">
            <div className="trucks2-titleRow">
              <h3 className="trucks2-plate" title={plate}>
                {plate}
              </h3>

              <span
                className={`trucks2-statusBadge ${
                  status.isActive ? "is-active" : "is-inactive"
                }`}
              >
                {status.label}
              </span>
            </div>

            <p className="trucks2-sub" title={sub}>
              {sub}
            </p>
          </div>
        </div>

        <div className="trucks2-topright">
          <button
            className="trucks2-actionBtn"
            type="button"
            aria-label={t("actions.edit_aria", "Editar")}
            title={t("actions.edit_title", "Editar")}
            onClick={(e) => {
              e.stopPropagation();
              onOpen?.(e);
            }}
          >
            <FaPen />
          </button>

          <button
            className="trucks2-actionBtn trucks2-actionBtn--danger"
            type="button"
            aria-label={t("row.actions.remove_aria", "Remover")}
            title={t("row.actions.remove_title", "Remover")}
            onClick={(e) => {
              e.stopPropagation();
              onRemove(e);
            }}
          >
            <FaTrashAlt />
          </button>
        </div>
      </header>

      <div className="trucks2-cardPanels">
        <section className="trucks2-panel">
          <div className="trucks2-panelTitle">
            <LuGauge />
            <span>{t("card.details", "Detalhes")}</span>
          </div>

          <DetailLine label={t("card.yearMonth", "Ano / Mês")} value={yearMonthText} />
          <DetailLine label={t("card.gas", "Tipo de gás")} value={gasType} />
          <DetailLine label={t("card.supportFuel", "Combustível de apoio")} value={supportFuel} />
          <DetailLine label={t("card.vin", "VIN")} value={vinText} mono />
        </section>

        <section className="trucks2-panel">
          <div className="trucks2-panelTitle">
            <LuFuel />
            <span>{t("card.consumption", "Consumo")}</span>
          </div>

          <div className="trucks2-consumptionHead">
            <span>{t("card.avgConsumption", "Consumo médio")}</span>

            <div className="trucks2-consumptionValueWrap">
              <strong>{fuelConsumptionText}</strong>
              <em className={`trucks2-consumptionBadge ${fuelStatusClass}`}>
                {fuelStatusText}
              </em>
            </div>
          </div>

          <div className="trucks2-fuelMiniPanel">
            <div className="trucks2-fuelMiniItem">
              <span>{t("fuel.lastDate", "Último registo")}</span>
              <strong>{lastFuelMetaText}</strong>
            </div>

            <div className="trucks2-fuelMiniItem">
              <span>{t("fuel.odometer", "Km")}</span>
              <strong>
                {lastOdometerText != null && String(lastOdometerText).trim() !== ""
                  ? `${lastOdometerText} km`
                  : "—"}
              </strong>
            </div>

            <div className="trucks2-fuelMiniItem">
              <span>{t("fuel.litres", "Litros")}</span>
              <strong>{fmtMaybeLitres(lastLitresText)}</strong>
            </div>

            <div className="trucks2-fuelMiniItem">
              <span>{t("fuel.totalCost", "Custo")}</span>
              <strong>{fmtMaybeMoney(lastTotalCostText)}</strong>
            </div>
          </div>
        </section>
      </div>

      <CardSection icon={LuFileCheck} title={t("card.docs", "Documentos (Datas & Validade)")}>
        <DocPanel rows={docsRows} />
      </CardSection>

      <CardSection icon={LuWrench} title={t("card.inventory", "Inventário")}>
        <InventoryGrid items={inventoryItems} />
      </CardSection>
    </article>
  );
});

const TrailerCard = React.memo(function TrailerCard({
  item,
  onOpen,
  onRemove,
  t,
}) {
  const iconToneClass = getIconToneClass(item?.id);
  const status = getVehicleStatus(item, t);

  const plate = pickValue(item?.trailerPlate, item?.trailerNumber, "—");
  const sub = pickValue(item?.brand, item?.trailer_brand, "—");
  const type = pickValue(item?.type, item?.trailerType, "—");

  const monthRaw = pickValue(item?.month, item?.Month, null);
  const yearMonthText =
    item?.year &&
    monthRaw !== null &&
    monthRaw !== undefined &&
    String(monthRaw).trim() !== ""
      ? `${String(item.year)} • ${monthName(monthRaw, t)}`
      : item?.year
      ? String(item.year)
      : "—";

  const docsRows = getTrailerDocRows(item, t);
  const inv = getTrailerInventory(item);

  const inventoryItems = [
    { label: t("card.axles", "Eixos"), value: inv.axles },
    { label: t("card.straps", "Nº de Cintas"), value: inv.straps },
    { label: t("card.ratchets", "Nº de Catracas"), value: inv.ratchets },
    { label: t("card.woodenBeams", "Vigas de Madeira"), value: inv.woodenBeams },
    { label: t("card.edge", "Nº de Cantos"), value: inv.edge },
    { label: t("card.antiSlip", "Nº de Antiderrapantes"), value: inv.antiSlip },
    { label: t("card.sidebars", "Barras Laterais"), value: inv.sidebars },
    { label: t("card.fireExtinguishers", "Extintores"), value: inv.fireExtinguishers },
  ];

  return (
    <article
      className="trucks2-card"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen?.(e)}
    >
      <header className="trucks2-top">
        <div className="trucks2-identity">
          <div className={`trucks2-iconbox ${iconToneClass}`}>
            <LuContainer />
          </div>

          <div className="trucks2-titlewrap">
            <div className="trucks2-titleRow">
              <h3 className="trucks2-plate" title={plate}>
                {plate}
              </h3>

              <span
                className={`trucks2-statusBadge ${
                  status.isActive ? "is-active" : "is-inactive"
                }`}
              >
                {status.label}
              </span>
            </div>

            <p className="trucks2-sub" title={sub}>
              {sub}
            </p>
          </div>
        </div>

        <div className="trucks2-topright">
          <button
            className="trucks2-actionBtn"
            type="button"
            aria-label={t("actions.edit_aria", "Editar")}
            title={t("actions.edit_title", "Editar")}
            onClick={(e) => {
              e.stopPropagation();
              onOpen?.(e);
            }}
          >
            <FaPen />
          </button>

          <button
            className="trucks2-actionBtn trucks2-actionBtn--danger"
            type="button"
            aria-label={t("row.actions.remove_aria", "Remover")}
            title={t("row.actions.remove_title", "Remover")}
            onClick={(e) => {
              e.stopPropagation();
              onRemove(e);
            }}
          >
            <FaTrashAlt />
          </button>
        </div>
      </header>

      <div className="trucks2-cardPanels trucks2-cardPanels--single">
        <section className="trucks2-panel">
          <div className="trucks2-panelTitle">
            <LuGauge />
            <span>{t("card.details", "Detalhes")}</span>
          </div>

          <DetailLine label={t("card.yearMonth", "Ano / Mês")} value={yearMonthText} />
          <DetailLine label={t("card.type", "Tipo de Reboque")} value={type} />
        </section>
      </div>

      <CardSection icon={LuFileCheck} title={t("card.docs", "Documentos (Datas & Validade)")}>
        <DocPanel rows={docsRows} />
      </CardSection>

      <CardSection icon={LuWrench} title={t("card.inventory", "Inventário")}>
        <InventoryGrid items={inventoryItems} />
      </CardSection>
    </article>
  );
});

/* ------------------------------ Page component ------------------------------ */
function Trucks() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("trucks");

  // Derive view from URL only
  const view = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("view") === "trailers" ? "trailers" : "trucks";
  }, [location.search]);

  const typeKey = view; // "trucks" | "trailers"
  const singularKey = view === "trucks" ? "truck" : "trailer";

  const [rawSearch, setRawSearch] = useState("");
  const searchTerm = useDebouncedValue(rawSearch, 180);
  const [errorMessage, setErrorMessage] = useState("");

  const PAGE_SIZE = 20;
  const [visibleLimit, setVisibleLimit] = useState(PAGE_SIZE);

  const isSearching = (searchTerm || "").trim().length > 0;

  const companyId = useMemo(() => resolveCompanyId(), []);

  const baseKey =
    companyId
      ? `company:${companyId}`
      : "company:unknown";

  const trucksSummaryCacheKey =
    `${baseKey}:trucks:summary:v2`;

  const trailersSummaryCacheKey =
    `${baseKey}:trailers:summary:v2`;

  const fuelSummariesCacheKey =
    `${baseKey}:truck-fuel-summaries:v1`;

  const initialCachedTrucks =
    useMemo(
      () =>
        readTruckSummaryCacheItems(
          trucksSummaryCacheKey
        ),
      [
        trucksSummaryCacheKey,
      ]
    );

  const initialCachedTrailers =
    useMemo(
      () => {
        const parsed =
          safeParse(
            window.localStorage.getItem(
              trailersSummaryCacheKey
            )
          );

        if (
          Array.isArray(
            parsed
          )
        ) {
          return parsed;
        }

        if (
          Array.isArray(
            parsed?.items
          )
        ) {
          return parsed.items;
        }

        if (
          Array.isArray(
            parsed?.data?.items
          )
        ) {
          return parsed.data.items;
        }

        return [];
      },
      [
        trailersSummaryCacheKey,
      ]
    );

  const [
    fuelSummaries,
    setFuelSummaries,
  ] = useState(
    () =>
      readFuelSummariesCache(
        fuelSummariesCacheKey
      )
  );

  const [
    fuelRefreshKey,
    setFuelRefreshKey,
  ] = useState(
    0
  );

  const etagRef = useRef({
    trucks:
      null,

    trailers:
      null,
  });

  const fetchTrucksSummary = useCallback(async ({ signal }) => {
    const res = await axios.get("/trucks/summary", {
      signal,
      validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
      headers: etagRef.current.trucks
        ? { "If-None-Match": etagRef.current.trucks }
        : undefined,
    });

    if (res.status === 304) {
      return { items: null, meta: { etag: etagRef.current.trucks } };
    }

    const items = Array.isArray(res.data) ? res.data : [];
    const etag = res.headers?.etag || null;
    etagRef.current.trucks = etag;
    return { items, meta: { etag } };
  }, []);

  const fetchTrailersSummary = useCallback(async ({ signal }) => {
    const res = await axios.get("/trailers/summary", {
      signal,
      validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
      headers: etagRef.current.trailers
        ? { "If-None-Match": etagRef.current.trailers }
        : undefined,
    });

    if (res.status === 304) {
      return { items: null, meta: { etag: etagRef.current.trailers } };
    }

    const items = Array.isArray(res.data) ? res.data : [];
    const etag = res.headers?.etag || null;
    etagRef.current.trailers = etag;
    return { items, meta: { etag } };
  }, []);

  const trucksCache = useInstantListCache({
    cacheKey:
      trucksSummaryCacheKey,

    fetcher:
      fetchTrucksSummary,

    initialItems:
      initialCachedTrucks,

    enabled:
      true,

    storage:
      "local",
  });

  const trailersCache = useInstantListCache({
    cacheKey:
      trailersSummaryCacheKey,

    fetcher:
      fetchTrailersSummary,

    initialItems:
      initialCachedTrailers,

    enabled:
      true,

    storage:
      "local",
  });

  const stableTrucksRef =
    useRef(
      initialCachedTrucks
    );

  const stableTrailersRef =
    useRef(
      initialCachedTrailers
    );

  useEffect(() => {
    if (
      Array.isArray(
        trucksCache.items
      ) &&
      (
        trucksCache.items.length >
          0 ||
        !trucksCache.loading
      )
    ) {
      stableTrucksRef.current =
        trucksCache.items;
    }
  }, [
    trucksCache.items,
    trucksCache.loading,
  ]);

  useEffect(() => {
    if (
      Array.isArray(
        trailersCache.items
      ) &&
      (
        trailersCache.items.length >
          0 ||
        !trailersCache.loading
      )
    ) {
      stableTrailersRef.current =
        trailersCache.items;
    }
  }, [
    trailersCache.items,
    trailersCache.loading,
  ]);

  const trucksForRender =
    Array.isArray(
      trucksCache.items
    ) &&
    trucksCache.items.length >
      0
      ? trucksCache.items
      : trucksCache.loading
      ? stableTrucksRef.current
      : Array.isArray(
          trucksCache.items
        )
      ? trucksCache.items
      : stableTrucksRef.current;

  const trailersForRender =
    Array.isArray(
      trailersCache.items
    ) &&
    trailersCache.items.length >
      0
      ? trailersCache.items
      : trailersCache.loading
      ? stableTrailersRef.current
      : Array.isArray(
          trailersCache.items
        )
      ? trailersCache.items
      : stableTrailersRef.current;

  useEffect(() => {
    const handleTruckCacheUpdated =
      (
        event
      ) => {
        const updatedTruck =
          event?.detail?.truck;

        if (
          !updatedTruck ||
          updatedTruck.id ===
            null ||
          updatedTruck.id ===
            undefined
        ) {
          return;
        }

        trucksCache.setItems(
          (
            previous
          ) => {
            const current =
              Array.isArray(
                previous
              )
                ? previous
                : [];

            const truckId =
              String(
                updatedTruck.id
              );

            const exists =
              current.some(
                (
                  item
                ) =>
                  String(
                    item?.id
                  ) ===
                  truckId
              );

            const next =
              exists
                ? current.map(
                    (
                      item
                    ) =>
                      String(
                        item?.id
                      ) ===
                      truckId
                        ? {
                            ...item,
                            ...updatedTruck,
                          }
                        : item
                  )
                : [
                    updatedTruck,
                    ...current,
                  ];

            next.sort(
              (
                first,
                second
              ) =>
                String(
                  first?.plate ||
                  ""
                ).localeCompare(
                  String(
                    second?.plate ||
                    ""
                  ),
                  undefined,
                  {
                    sensitivity:
                      "base",
                  }
                )
            );

            return next;
          }
        );

        etagRef.current.trucks =
          null;
      };

    window.addEventListener(
      TRUCK_SUMMARY_CACHE_UPDATED_EVENT,
      handleTruckCacheUpdated
    );

    return () => {
      window.removeEventListener(
        TRUCK_SUMMARY_CACHE_UPDATED_EVENT,
        handleTruckCacheUpdated
      );
    };
  }, [
    trucksCache.setItems,
  ]);

  useEffect(() => {
    if (trucksCache.meta?.etag) etagRef.current.trucks = trucksCache.meta.etag;
    if (trailersCache.meta?.etag)
      etagRef.current.trailers = trailersCache.meta.etag;
  }, [trucksCache.meta, trailersCache.meta]);

  // -------------------- warm details cache (instant EditTruck) --------------------
  useEffect(() => {
    if (
      view !==
      "trucks"
    ) {
      return;
    }

    const list =
      trucksForRender;

    if (
      list.length ===
      0
    ) {
      return;
    }

    const ids =
      list
        .slice(
          0,
          40
        )
        .map(
          (
            item
          ) =>
            item?.id
        )
        .filter(
          Boolean
        );

    const controller =
      new AbortController();

    let cancelled =
      false;

    const CONCURRENCY =
      4;

    const run =
      async () => {
        const queue =
          ids.filter(
            (
              id
            ) =>
              !isTruckDetailsCacheFresh(
                id
              )
          );

        const worker =
          async () => {
            while (
              !cancelled &&
              queue.length
            ) {
              const id =
                queue.shift();

              if (
                !id
              ) {
                continue;
              }

              const already =
                readTruckDetailsCache(
                  id
                );

              if (
                already &&
                isTruckDetailsCacheFresh(
                  id
                )
              ) {
                continue;
              }

              try {
                const {
                  data,
                } =
                  await axios.get(
                    `/trucks/${id}`,
                    {
                      signal:
                        controller.signal,
                    }
                  );

                if (
                  cancelled
                ) {
                  return;
                }

                writeTruckDetailsCache(
                  id,
                  data
                );
              } catch (
                error
              ) {
                if (
                  error?.name ===
                    "CanceledError" ||
                  error?.code ===
                    "ERR_CANCELED"
                ) {
                  return;
                }

                console.warn(
                  `[Trucks] failed to warm truck ${id}:`,
                  error
                );
              }
            }
          };

        await Promise.all(
          Array.from(
            {
              length:
                CONCURRENCY,
            },
            () =>
              worker()
          )
        );
      };

    run();

    return () => {
      cancelled =
        true;

      controller.abort();
    };
  }, [
    view,
    trucksForRender,
  ]);

  // -------------------- warm details cache (instant EditTrailer) --------------------
  const TRAILER_DETAILS_TTL_MS = 1000 * 60 * 60 * 24;
  const trailerDetailsTsKey = (id) => `trailer:detail:ts:${id}`;
  const trailerDetailsKey = (id) => `trailer:detail:${id}`;

  const trailerIsFresh = (id) => {
    const ts = Number(localStorage.getItem(trailerDetailsTsKey(id)) || 0);
    return ts && Date.now() - ts < TRAILER_DETAILS_TTL_MS;
  };

  const saveTrailerDetails = (id, data) => {
    try {
      localStorage.setItem(trailerDetailsKey(id), JSON.stringify(data));
      localStorage.setItem(trailerDetailsTsKey(id), String(Date.now()));
    } catch {}
  };

  useEffect(() => {
    if (view !== "trailers") return;
    const list = trailersCache.items || [];
    if (list.length === 0) return;

    const ids = list.slice(0, 40).map((x) => x.id).filter(Boolean);

    const ctrl = new AbortController();
    let cancelled = false;
    const CONCURRENCY = 4;

    const run = async () => {
      const queue = [...ids].filter((id) => !trailerIsFresh(id));

      const worker = async () => {
        while (!cancelled && queue.length) {
          const id = queue.shift();
          if (!id) continue;

          const already = safeParse(localStorage.getItem(trailerDetailsKey(id)));
          if (already && trailerIsFresh(id)) continue;

          try {
            const { data } = await axios.get(`/trailers/${id}`, {
              signal: ctrl.signal,
            });
            if (cancelled) return;
            saveTrailerDetails(id, data);
          } catch {}
        }
      };

      await Promise.all(Array.from({ length: CONCURRENCY }, worker));
    };

    run();

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [view, trailersCache.items]);

  // 🔌 Live updates (Socket.IO) for trucks tab
  useEffect(() => {
    if (view !== "trucks") return;

    const s = getSocket();

    const subscribe = () => {
      s.emit("subscribe:trucks");
    };

    subscribe();
    s.on("connect", subscribe);
    s.on("reconnect", subscribe);

    const onCreated = (trow) => {
      primeTruckDetailsCache(
        trow?.id,
        trow
      );

      trucksCache.setItems((prev) => {
        const next = prev.some((x) => String(x.id) === String(trow.id))
          ? prev
          : [trow, ...prev];
        next.sort((a, b) =>
          (a.plate || "").localeCompare(b.plate || "", undefined, {
            sensitivity: "base",
          })
        );
        return next;
      });
      etagRef.current.trucks = null;
    };

    const onUpdated = (trow) => {
      primeTruckDetailsCache(
        trow?.id,
        trow
      );

      trucksCache.setItems((prev) => {
        const next = prev.map((x) =>
          String(x.id) === String(trow.id) ? { ...x, ...trow } : x
        );
        next.sort((a, b) =>
          (a.plate || "").localeCompare(b.plate || "", undefined, {
            sensitivity: "base",
          })
        );
        return next;
      });
      etagRef.current.trucks = null;
    };

    const onDeleted = ({ id }) => {
      trucksCache.setItems((prev) =>
        prev.filter((x) => String(x.id) !== String(id))
      );
      etagRef.current.trucks = null;
    };

    const onStale = () => {
      etagRef.current.trucks = null;
    };

    const onFuelLogsUpdated = () => {
      /*
       * Mantemos o valor atual visível enquanto
       * o novo resumo é pedido ao backend.
       */
      setFuelRefreshKey(
        (
          current
        ) =>
          current +
          1
      );
    };

    s.on("truck:created", onCreated);
    s.on("truck:updated", onUpdated);
    s.on("truck:deleted", onDeleted);
    s.on("trucks:summary:stale", onStale);
    s.on("truck-fuel-logs:updated", onFuelLogsUpdated);

    return () => {
      s.emit("unsubscribe:trucks");
      s.off("connect", subscribe);
      s.off("reconnect", subscribe);
      s.off("truck:created", onCreated);
      s.off("truck:updated", onUpdated);
      s.off("truck:deleted", onDeleted);
      s.off("trucks:summary:stale", onStale);
      s.off("truck-fuel-logs:updated", onFuelLogsUpdated);
    };
  }, [view]);

  // 🔌 Live updates (Socket.IO) for trailers tab
  useEffect(() => {
    if (view !== "trailers") return;

    const s = getSocket();

    const subscribe = () => {
      s.emit("subscribe:trailers");
    };

    subscribe();
    s.on("connect", subscribe);
    s.on("reconnect", subscribe);

    const normalize = (r) => ({
      ...r,
      trailerPlate: r.trailerPlate ?? r.trailerNumber,
    });

    const onCreated = (row) => {
      const trow = normalize(row);
      trailersCache.setItems((prev) => {
        const next = prev.some((x) => String(x.id) === String(trow.id))
          ? prev
          : [trow, ...prev];
        next.sort((a, b) =>
          (a.trailerPlate || "").localeCompare(
            b.trailerPlate || "",
            undefined,
            { sensitivity: "base" }
          )
        );
        return next;
      });
      etagRef.current.trailers = null;
    };

    const onUpdated = (row) => {
      const trow = normalize(row);
      trailersCache.setItems((prev) => {
        const next = prev.map((x) =>
          String(x.id) === String(trow.id) ? { ...x, ...trow } : x
        );
        next.sort((a, b) =>
          (a.trailerPlate || "").localeCompare(
            b.trailerPlate || "",
            undefined,
            { sensitivity: "base" }
          )
        );
        return next;
      });
      etagRef.current.trailers = null;
    };

    const onDeleted = ({ id }) => {
      trailersCache.setItems((prev) =>
        prev.filter((x) => String(x.id) !== String(id))
      );
      etagRef.current.trailers = null;
    };

    const onStale = () => {
      etagRef.current.trailers = null;
    };

    s.on("trailer:created", onCreated);
    s.on("trailer:updated", onUpdated);
    s.on("trailer:deleted", onDeleted);
    s.on("trailers:summary:stale", onStale);

    return () => {
      s.emit("unsubscribe:trailers");
      s.off("connect", subscribe);
      s.off("reconnect", subscribe);
      s.off("trailer:created", onCreated);
      s.off("trailer:updated", onUpdated);
      s.off("trailer:deleted", onDeleted);
      s.off("trailers:summary:stale", onStale);
    };
  }, [view]);

  /* -------------------------- Handlers -------------------------- */
  const handleRemove = useCallback(
    async (id) => {
      const confirmDelete = window.confirm(
        t(`confirm.delete.${singularKey}`)
      );
      if (!confirmDelete) return;
      try {
        await axios.delete(`/${view}/${id}`);
        if (view === "trucks") {
          trucksCache.setItems((prev) =>
            prev.filter((x) => String(x.id) !== String(id))
          );
        } else {
          trailersCache.setItems((prev) =>
            prev.filter((x) => String(x.id) !== String(id))
          );
        }
        etagRef.current[view] = null;
      } catch (error) {
        setErrorMessage(t(`errors.delete_failed.${singularKey}`));
      }
    },
    [view, t, singularKey]
  );

  const handleEdit = useCallback(
    async (
      itemOrId
    ) => {
      const id =
        typeof itemOrId ===
        "object"
          ? itemOrId?.id
          : itemOrId;

      if (
        !id
      ) {
        return;
      }

      const editPath =
        view ===
        "trucks"
          ? `/trucks/${id}/edit`
          : `/trailers/${id}/edit`;

      const row =
        typeof itemOrId ===
        "object"
          ? itemOrId
          : view ===
            "trucks"
          ? trucksForRender.find(
              (
                item
              ) =>
                String(
                  item?.id
                ) ===
                String(
                  id
                )
            )
          : trailersForRender.find(
              (
                item
              ) =>
                String(
                  item?.id
                ) ===
                String(
                  id
                )
            );

      /*
       * TRUCK
       *
       * Só abrimos o EditTruck diretamente
       * quando sabemos que o cache veio de
       * um GET completo /trucks/:id.
       */
      if (
        view ===
        "trucks"
      ) {
        const cached =
          readTruckDetailsCache(
            id
          );

        if (
          cached &&
          isTruckDetailsCacheFresh(
            id
          )
        ) {
          navigate(
            editPath,
            {
              state: {
                prefill:
                  cached,
              },
            }
          );

          return;
        }

        /*
         * Ainda não temos detalhe completo.
         *
         * Em vez de abrir o EditTruck vazio/parcial
         * e preencher depois, obtemos primeiro os
         * dados completos e só então navegamos.
         */
        try {
          /*
           * Preparamos os dados completos do camião
           * e a secção de combustível antes de abrir
           * o EditTruck.
           */
          const fuelCacheKey =
            `truck:fuel:details:v1:${id}`;

          let cachedFuel =
            null;

          try {
            const rawFuel =
              window.localStorage.getItem(
                fuelCacheKey
              );

            cachedFuel =
              rawFuel
                ? JSON.parse(
                    rawFuel
                  )
                : null;
          } catch {
            cachedFuel =
              null;
          }

          const truckRequest =
            axios.get(
              `/trucks/${id}`,
              {
                params: {
                  _editPrefill:
                    Date.now(),
                },

                headers: {
                  "Cache-Control":
                    "no-cache",

                  Pragma:
                    "no-cache",
                },
              }
            );

          const fuelRequest =
            cachedFuel
              ? Promise.resolve(
                  null
                )
              : axios.get(
                  `/truck-fuel-logs/truck/${id}`
                );

          const [
            truckResponse,
            fuelResponse,
          ] =
            await Promise.all([
              truckRequest,
              fuelRequest,
            ]);

          const data =
            truckResponse.data;

          writeTruckDetailsCache(
            id,
            data
          );

          /*
           * Se ainda não tínhamos combustível
           * guardado, guardamo-lo antes da navegação.
           */
          if (
            fuelResponse
              ?.data
          ) {
            try {
              window.localStorage.setItem(
                fuelCacheKey,
                JSON.stringify({
                  logs:
                    Array.isArray(
                      fuelResponse.data
                        ?.logs
                    )
                      ? fuelResponse.data.logs
                      : [],

                  summary:
                    fuelResponse.data
                      ?.summary ||
                    null,
                })
              );

              window.localStorage.setItem(
                `truck:fuel:details:ts:v1:${id}`,
                String(
                  Date.now()
                )
              );
            } catch {
              // Cache indisponível.
            }
          }

          /*
           * Só agora abrimos o EditTruck.
           *
           * Portanto TruckFuelTab também já nasce
           * preenchido no primeiro render.
           */
          navigate(
            editPath,
            {
              state: {
                prefill:
                  data,
              },
            }
          );

          return;
        } catch (
          error
        ) {
          console.error(
            "[Trucks] failed to prepare EditTruck:",
            error
          );

          /*
           * Fallback apenas caso o backend falhe.
           * Em condições normais não chegamos aqui.
           */
          if (
            cached
          ) {
            navigate(
              editPath,
              {
                state: {
                  prefill:
                    cached,
                },
              }
            );

            return;
          }

          if (
            row
          ) {
            navigate(
              editPath,
              {
                state: {
                  prefill:
                    row,
                },
              }
            );

            return;
          }

          navigate(
            editPath
          );

          return;
        }
      }

      /*
       * TRAILER
       *
       * Mantemos o comportamento atual.
       */
      const detailKey =
        `trailer:detail:${id}`;

      const cached =
        safeParse(
          localStorage.getItem(
            detailKey
          )
        );

      if (
        cached
      ) {
        navigate(
          editPath,
          {
            state: {
              prefill:
                cached,
            },
          }
        );

        return;
      }

      if (
        row
      ) {
        navigate(
          editPath,
          {
            state: {
              prefill:
                row,
            },
          }
        );

        return;
      }

      navigate(
        editPath
      );
    },
    [
      navigate,
      view,
      trucksForRender,
      trailersForRender,
    ]
  );

  const stopProp = useCallback((e) => e.stopPropagation(), []);

  /* ------------------------------- Filtering ------------------------------- */
  const filteredItems = useMemo(() => {
    const list =
      view === "trucks"
        ? trucksForRender
        : trailersForRender;
    const q = (searchTerm || "").trim().toLowerCase();
    if (!q) return list;

    if (view !== "trucks") {
      return list.filter((item) =>
        (item.trailerPlate || item.trailerNumber || "").toLowerCase().includes(q)
      );
    }

    return list.filter((item) => {
      const plate = String(item.plate || "").toLowerCase();
      const brand = String(item.brand || "").toLowerCase();
      const model = String(item.model || "").toLowerCase();
      const vin = String(item.vin || "").toLowerCase();
      const gas = String(item.type_of_gas || "").toLowerCase();
      const year = String(item.year || "").toLowerCase();
      return (
        plate.includes(q) ||
        brand.includes(q) ||
        model.includes(q) ||
        vin.includes(q) ||
        gas.includes(q) ||
        year.includes(q)
      );
    });
  }, [
    view,
    trucksForRender,
    trailersForRender,
    searchTerm,
  ]);

  const totalCount =
    (
      view === "trucks"
        ? trucksForRender
        : trailersForRender
    ).length;

  const filteredCount = filteredItems.length;
  const renderedCount = isSearching
    ? filteredCount
    : Math.min(visibleLimit, filteredCount);

  const visibleItems = useMemo(() => {
    return isSearching ? filteredItems : filteredItems.slice(0, visibleLimit);
  }, [filteredItems, visibleLimit, isSearching]);

  const truckSummaryIds = useMemo(() => {
    if (view !== "trucks") return [];

    return trucksForRender
      .slice(0, 300)
      .map((item) => Number(item?.id))
      .filter((id) => Number.isInteger(id) && id > 0);
  }, [
    view,
    trucksForRender,
  ]);

  const truckSummaryIdsKey = truckSummaryIds.join(",");

  useEffect(() => {
    if (view !== "trucks") return;
    if (!truckSummaryIdsKey) return;

    const ctrl = new AbortController();

    const fetchFuelSummaries = async () => {
      try {
        const { data } = await axios.get("/truck-fuel-logs/summary/by-trucks", {
          signal: ctrl.signal,
          params: {
            truckIds: truckSummaryIdsKey,
          },
        });

        const incoming = data?.summaries || {};

        setFuelSummaries((prev) => ({
          ...prev,
          ...incoming,
        }));
      } catch (error) {
        if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
          return;
        }

        console.error("[Trucks] fuel summaries fetch failed:", error);
      }
    };

    fetchFuelSummaries();

    return () => {
      ctrl.abort();
    };
  }, [view, truckSummaryIdsKey, fuelRefreshKey]);

  const activeItems = useMemo(() => {
    return view === "trucks"
      ? trucksForRender
      : trailersForRender;
  }, [
    view,
    trucksForRender,
    trailersForRender,
  ]);

  const fleetStats = useMemo(() => {
    const total = activeItems.length;

    const calculatedConsumptionCount =
      view === "trucks"
        ? activeItems.reduce((acc, item) => {
            const summary = fuelSummaries[String(item?.id)] || null;
            return acc + (hasCalculatedConsumption(summary) ? 1 : 0);
          }, 0)
        : 0;

    const inventoryCount = activeItems.reduce(
      (acc, item) => acc + (hasInventory(item, view) ? 1 : 0),
      0
    );

    const secondaryValue =
      view === "trucks" ? calculatedConsumptionCount : inventoryCount;

    const secondaryPct = total > 0 ? Math.round((secondaryValue / total) * 100) : 0;

    return {
      total,
      secondaryValue,
      secondaryTitle:
        view === "trucks"
          ? t("stats.consumptionCalculated", "Com consumo calculado")
          : t("stats.withInventory", "Com inventário registado"),
      secondarySubtitle:
        view === "trucks"
          ? t("stats.ofTotal", { pct: secondaryPct, defaultValue: "{{pct}}% do total" })
          : t("stats.inventorySubtitle", {
              pct: secondaryPct,
              defaultValue: "{{pct}}% do total",
            }),
      docsExpiring: countExpiringDocs(activeItems, view),
      attentionItems: countAttentionItems(activeItems, view),
    };
  }, [activeItems, fuelSummaries, view, t]);

  const hasItems = visibleItems.length > 0;

  useEffect(() => {
    setVisibleLimit(PAGE_SIZE);
  }, [view, searchTerm]);

  useEffect(() => {
    writeFuelSummariesCache(
      fuelSummariesCacheKey,
      fuelSummaries
    );
  }, [
    fuelSummariesCacheKey,
    fuelSummaries,
  ]);

  return (
    <div className="trucks2-page">
      {/* Header */}
      <section className="trucks2-toolbar">
        <div className="trucks2-toolbarLeft">
          <div className="trucks2-toolbarHeading">
            <h1 className="trucks2-toolbarHeading__title">
              {view === "trucks"
                ? t("header.trucksTitle", "Camiões")
                : t("header.trailersTitle", "Reboques")}
            </h1>
            <p className="trucks2-toolbarHeading__subtitle">
              {view === "trucks"
                ? t("header.trucksRegistered", {
                    count: totalCount,
                    defaultValue: "{{count}} camiões registados",
                  })
                : t("header.trailersRegistered", {
                    count: totalCount,
                    defaultValue: "{{count}} reboques registados",
                  })}
            </p>
          </div>
        </div>

        {/* Toggle */}
        <div className="trucks2-toggleWrap">
          <div className="trucks2-toggle" data-view={view}>
            <div className="trucks2-toggleSlider" data-view={view} />
            <button
              className={`trucks2-toggleBtn ${
                view === "trucks" ? "active" : ""
              }`}
              onClick={() => navigate("/trucks?view=trucks")}
              type="button"
            >
              {t("toggle.trucks")}
            </button>
            <button
              className={`trucks2-toggleBtn ${
                view === "trailers" ? "active" : ""
              }`}
              onClick={() => navigate("/trucks?view=trailers")}
              type="button"
            >
              {t("toggle.trailers")}
            </button>
          </div>
        </div>

        <div className="trucks2-toolbarRight">
          <div className="trucks2-search">
            <FaSearch className="trucks2-searchIcon" />
            <input
              type="text"
              className="trucks2-searchInput"
              placeholder={t(`search.placeholder.${typeKey}`)}
              value={rawSearch}
              onChange={(e) => setRawSearch(e.target.value)}
              aria-label={t("search.aria")}
            />
            {rawSearch && (
              <button
                type="button"
                className="trucks2-searchClear"
                onClick={() => setRawSearch("")}
                aria-label={t("search.clear")}
                title={t("search.clear")}
              >
                ✕
              </button>
            )}
          </div>

          <button
            className="trucks2-primaryBtn"
            onClick={() => navigate(`/${view}/create`)}
            type="button"
          >
            {view === "trucks"
              ? t("buttons.new_truck")
              : t("buttons.new_trailer")}
          </button>
        </div>
      </section>

      <section className="trucks2-overviewGrid">
        <FleetStatCard
          tone="blue"
          icon={view === "trucks" ? LuTruck : LuContainer}
          title={t("stats.totalVehicles", "Total de veículos")}
          value={fleetStats.total}
          subtitle={t("stats.fleetSubtitle", "100% da frota")}
        />

        <FleetStatCard
          tone="green"
          icon={view === "trucks" ? LuFuel : LuWrench}
          title={fleetStats.secondaryTitle}
          value={fleetStats.secondaryValue}
          subtitle={fleetStats.secondarySubtitle}
        />

        <FleetStatCard
          tone="amber"
          icon={LuFileCheck}
          title={t("stats.expiringDocs", "Documentos a expirar")}
          value={fleetStats.docsExpiring}
          subtitle={t("stats.next60Days", "Próx. 60 dias")}
        />

        <FleetStatCard
          tone="red"
          icon={FaExclamationTriangle}
          title={t("stats.needsAttention", "Necessitam atenção")}
          value={fleetStats.attentionItems}
          subtitle={t("stats.pendingItems", "Itens pendentes")}
        />
      </section>

      {/* Error */}
      {errorMessage && (
        <section className="trucks2-error">
          <div className="trucks2-errorText">{errorMessage}</div>
        </section>
      )}

      {/* Cards */}
      <section className="trucks2-content">
        {hasItems ? (
          <>
            <div className="trucks2-grid">
              {visibleItems.map((item) => {
                const id = item?.id;
                if (!id) return null;

                return view === "trucks" ? (
                  <TruckCard
                    key={`truck-${id}`}
                    item={item}
                    fuelSummary={fuelSummaries[String(id)] || null}
                    t={t}
                    stopProp={stopProp}
                    onOpen={() => handleEdit(item)}
                    onRemove={(e) => {
                      e?.stopPropagation?.();
                      handleRemove(id);
                    }}
                  />
                ) : (
                  <TrailerCard
                    key={`trailer-${id}`}
                    item={item}
                    t={t}
                    stopProp={stopProp}
                    onOpen={() => handleEdit(item)}
                    onRemove={(e) => {
                      e?.stopPropagation?.();
                      handleRemove(id);
                    }}
                  />
                );
              })}
            </div>

            {!isSearching && renderedCount < filteredCount && (
              <div className="trucks2-loadMoreWrap">
                <button
                  className="trucks2-loadMoreBtn"
                  type="button"
                  onClick={() =>
                    setVisibleLimit((v) =>
                      Math.min(v + PAGE_SIZE, filteredCount)
                    )
                  }
                >
                  {t("buttons.load_more", "Load more")}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="trucks2-empty">
            <div className="trucks2-emptyTitle">{t(`empty.title.${typeKey}`)}</div>
            <div className="trucks2-emptySubtitle">
              {t(`empty.subtitle.${typeKey}`)}
            </div>
            <button
              className="trucks2-primaryBtn"
              onClick={() => navigate(`/${view}/create`)}
              type="button"
            >
              {view === "trucks"
                ? t("buttons.add_truck")
                : t("buttons.add_trailer")}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

export default Trucks;